import React, { useMemo, useRef, useState } from 'react';
import type { SelectProps } from 'antd';
import { Select, Spin } from 'antd';
import debounce from 'lodash/debounce';

export interface DebounceSelectProps<ValueType = unknown>
  extends Omit<SelectProps<ValueType | ValueType[]>, 'options' | 'children'> {
  fetchOptions: (search: string) => Promise<ValueType[]>;
  debounceTimeout?: number;
}

export default function DebounceSelect<
  ValueType extends {
    id?: string;
    displayName?: React.ReactNode;
    email?: string | number;
    type?: string;
    value?: string;
    label?: string;
    key?: string;
  },
>({ fetchOptions, debounceTimeout = 300, ...props }: DebounceSelectProps<ValueType>) {
  const [fetching, setFetching] = useState(false);
  const [options, setOptions] = useState<ValueType[]>([]);
  const fetchRef = useRef(0);

  const debounceFetcher = useMemo(() => {
    const loadOptions = (value: string) => {
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setOptions([]);
      setFetching(true);

      fetchOptions(value).then((newOptions) => {
        if (fetchId !== fetchRef.current) {
          return;
        }

        setOptions(newOptions);
        setFetching(false);
      });
    };

    return debounce(loadOptions, debounceTimeout);
  }, [fetchOptions, debounceTimeout]);

  return (
    <Select
      labelInValue
      filterOption={false}
      onSearch={debounceFetcher}
      notFoundContent={fetching ? <Spin size="small" /> : 'No results found'}
      {...props}
      options={options.map((option) => ({
        value: option.id,
        label: option.displayName,
        entraId: option.id,
        email: option.email,
        key: option.email,
        ...option,
      }))}
      optionRender={(option) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>{option.label}</div>
      )}
    />
  );
}